/**
 * Block 13 PR-G Phase 5B — Responsive «Переслать клиенту» bottom-sheet для
 * договора (Экран 2.13.3 finale).
 *
 * Mirror Block 12 `ShareBottomSheet.tsx` pattern (PR-D-B Phase G1):
 *   - **Mobile (< 768px)** — single «Поделиться» CTA → `shareContractPdf` с
 *     `channel: 'auto'`. Service internally делает `canShareFiles({files})` +
 *     fallback на download если capability отсутствует.
 *   - **Desktop (≥ 768px)** — 5-button grid (WhatsApp / Telegram / MAX /
 *     Mail / Gmail). Каждая CTA → `shareContractPdf` с explicit channel.
 *     Block 12 G4 lesson: desktop native share неадекватно мапит installed
 *     мессенджеры (Unigram label вместо Telegram, MAX отсутствует, Mail/Gmail
 *     не surface) — custom buttons обеспечивают predictable UX.
 *
 * **Capability detection through media query, NOT navigator.canShare** (per
 * Aibek Confirmation 3): отделяет split decision (UI layout) от capability
 * test (внутри `shareContractPdf` service). Window resize re-renders sheet
 * через reactive `useMediaQuery` hook.
 *
 * **Locale-aware share text** уже handled в `shareContractPdf` service per
 * `template.meta.lang` (ru/kk/uz) — sheet просто wires UI → service.
 *
 * **Design system (P-038..P-048):**
 *   - bg-card на sheet body (P-038) — inherited из BottomSheet primitive
 *   - text-ink на labels (P-039)
 *   - bg-cta + text-ink на primary CTA (mobile «Поделиться» + desktop
 *     channel buttons — primary action allowlist per P-041 для buttons)
 *   - icon circles используют brand colors мессенджеров (бренд-цвета — не
 *     наш palette, корректное использование per ShareBottomSheet precedent)
 *   - min-h-tap + min-w-tap (P-044)
 *
 * Reference:
 *   - `apps/web/src/features/calculator/quote/components/ShareBottomSheet.tsx`
 *     (Block 12 pattern — copy structure, adapt to contract shareContractPdf
 *      service signature)
 *   - ADR-0025 §Phase 5 ShareContractBottomSheet
 *   - Block_13_PR_G_phase_0_planning.md Phase 5 (5-channel desktop deferred
 *     from Phase 4)
 */

import { useState, type ReactElement } from 'react';
import { Share2, Mail } from 'lucide-react';

import { BottomSheet } from '@/components/BottomSheet/BottomSheet';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { ContractDraft, ContractTemplate } from '@pomoshnik/shared';

import {
  shareContractPdf,
  type ContractShareChannel,
  type ContractShareResult,
} from '../services/shareContractPdf';

export interface ShareContractBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  draft: ContractDraft;
  template: ContractTemplate;
  /** Notification handler — parent отображает confirmation/error toast. */
  onResult?: (result: ContractShareResult) => void;
}

type ShareStatus = 'idle' | 'sharing' | 'error';

interface ChannelConfig {
  channel: ContractShareChannel;
  label: string;
  testId: string;
  ariaLabel: string;
  bg: string;
  Icon: () => ReactElement;
}

const DESKTOP_CHANNEL_CONFIGS: readonly ChannelConfig[] = [
  {
    channel: 'whatsapp',
    label: 'WhatsApp',
    testId: 'share-contract-whatsapp-button',
    ariaLabel: 'Поделиться договором через WhatsApp',
    bg: '#25D366',
    Icon: WhatsAppIcon,
  },
  {
    channel: 'telegram',
    label: 'Telegram',
    testId: 'share-contract-telegram-button',
    ariaLabel: 'Поделиться договором через Telegram',
    bg: '#229ED9',
    Icon: TelegramIcon,
  },
  {
    channel: 'max',
    label: 'MAX',
    testId: 'share-contract-max-button',
    ariaLabel: 'Поделиться договором через MAX',
    bg: '#1A1A1A',
    Icon: MaxIcon,
  },
  {
    channel: 'mail',
    label: 'Mail',
    testId: 'share-contract-mail-button',
    ariaLabel: 'Поделиться договором через Mail',
    bg: '#6B7280',
    Icon: MailIconWrapper,
  },
  {
    channel: 'gmail',
    label: 'Gmail',
    testId: 'share-contract-gmail-button',
    ariaLabel: 'Поделиться договором через Gmail',
    bg: '#EA4335',
    Icon: GmailIcon,
  },
] as const;

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" fill="#FFFFFF">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.768.967-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
    </svg>
  );
}

function TelegramIcon() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" fill="#FFFFFF">
      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
    </svg>
  );
}

function MaxIcon() {
  return (
    <span
      aria-hidden="true"
      className="text-white font-display text-2xl leading-none tracking-tight"
      style={{ fontWeight: 700 }}
    >
      MAX
    </span>
  );
}

function MailIconWrapper() {
  return <Mail size={28} color="#FFFFFF" aria-hidden="true" />;
}

function GmailIcon() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" fill="#FFFFFF">
      <path d="M22 5.457v13.086A1.457 1.457 0 0 1 20.543 20H19V8.236l-7 4.97-7-4.97V20H3.457A1.457 1.457 0 0 1 2 18.543V5.457A1.457 1.457 0 0 1 3.457 4h.343l8.2 5.823L20.2 4h.343A1.457 1.457 0 0 1 22 5.457Z" />
    </svg>
  );
}

export function ShareContractBottomSheet({
  isOpen,
  onClose,
  draft,
  template,
  onResult,
}: ShareContractBottomSheetProps) {
  const [status, setStatus] = useState<ShareStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  async function runShare(channel: ContractShareChannel): Promise<void> {
    setStatus('sharing');
    setErrorMessage(null);

    const result = await shareContractPdf(draft, template, { channel });

    if (import.meta.env.DEV && result.status === 'error') {
      // P-009: log only err.name (avoid potential PII leak в error message).
      const errName = result.error instanceof Error ? result.error.name : 'unknown';
      console.error('[ShareContractBottomSheet] shareContractPdf error', errName);
    }

    function notify(r: ContractShareResult): void {
      try {
        onResult?.(r);
      } catch (callbackErr) {
        if (import.meta.env.DEV) {
          console.error('[ShareContractBottomSheet] onResult threw', callbackErr);
        }
      }
    }

    if (result.status === 'error') {
      setStatus('error');
      setErrorMessage('Ошибка — попробуйте ещё раз');
      notify(result);
      return;
    }

    setStatus('idle');
    setErrorMessage(null);
    notify(result);
    onClose();
  }

  const isBusy = status === 'sharing';

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Переслать клиенту" maxHeightVh={70}>
      {isDesktop ? (
        <>
          <p
            className="text-ink/60 text-sm mb-4 text-center"
            data-testid="share-contract-helper-text"
          >
            Выберите мессенджер или почтовый клиент.
          </p>

          <div
            className="grid grid-cols-5 gap-3 mb-4"
            role="group"
            aria-label="Каналы расшаривания договора"
            data-testid="share-contract-channels-desktop"
          >
            {DESKTOP_CHANNEL_CONFIGS.map((cfg) => (
              <button
                key={cfg.channel}
                type="button"
                onClick={() => runShare(cfg.channel)}
                disabled={isBusy}
                aria-label={cfg.ariaLabel}
                aria-busy={isBusy}
                data-testid={cfg.testId}
                className="flex flex-col items-center gap-2 px-1 py-2 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
              >
                <span
                  aria-hidden="true"
                  style={{ backgroundColor: cfg.bg }}
                  className="w-[60px] h-[60px] rounded-full flex items-center justify-center shadow-card"
                >
                  <cfg.Icon />
                </span>
                <span className="text-ink text-sm font-medium">{cfg.label}</span>
              </button>
            ))}
          </div>

          <p
            className="text-ink/60 text-xs text-center mb-4"
            data-testid="share-contract-clipboard-hint"
          >
            После клика откроется мессенджер в новой вкладке, PDF скачается отдельно — прикрепите
            его к сообщению.
          </p>
        </>
      ) : (
        <>
          <p className="text-ink/60 text-sm mb-4" data-testid="share-contract-helper-text">
            Откроется системное окно «Поделиться». Если устройство не поддерживает прикрепление
            файла — PDF скачается, текст сообщения подготовлен.
          </p>

          <button
            type="button"
            onClick={() => runShare('auto')}
            disabled={isBusy}
            aria-label="Поделиться договором"
            aria-busy={isBusy}
            data-testid="share-contract-button"
            className="w-full min-h-tap px-4 mb-4 rounded-2xl bg-cta text-ink text-lg font-semibold flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80"
          >
            <Share2 size={22} aria-hidden="true" />
            <span aria-live="polite">{isBusy ? 'Подготавливаем…' : 'Поделиться'}</span>
          </button>
        </>
      )}

      {errorMessage ? (
        <p
          role="alert"
          data-testid="share-contract-error"
          className="text-sm text-destructive font-medium text-center mb-3"
        >
          {errorMessage}
        </p>
      ) : null}

      <button
        type="button"
        onClick={onClose}
        disabled={isBusy}
        aria-label="Закрыть окно пересылки договора"
        className="w-full min-h-tap rounded-xl border-[1.5px] border-accent bg-card text-ink text-base font-medium disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
      >
        Отмена
      </button>
    </BottomSheet>
  );
}
